/**
 * Renders the row actions (see participantActions.ts) in two places: the ⋮ dropdown and the
 * right-click menu over the row. Both draw the same groups, so they cannot drift apart.
 * Groups are separated by a line; titles are omitted to keep the menu compact.
 */

import { Fragment, cloneElement, useState, type ComponentType, type MouseEventHandler, type ReactElement, type ReactNode } from 'react';
import { MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { ParticipantActionGroup } from './participantActions';

interface MenuParts {
  Item: ComponentType<{ asChild?: boolean; onClick?: MouseEventHandler<HTMLDivElement>; className?: string; children?: ReactNode }>;
  Separator: ComponentType;
}

const DROPDOWN_PARTS: MenuParts = { Item: DropdownMenuItem, Separator: DropdownMenuSeparator };
const CONTEXT_PARTS: MenuParts = { Item: ContextMenuItem, Separator: ContextMenuSeparator };

function MenuItems({ groups, parts }: { groups: ParticipantActionGroup[]; parts: MenuParts }) {
  const { Item, Separator } = parts;
  return (
    <>
      {groups.map((group, i) => (
        <Fragment key={group.id}>
          {i > 0 && <Separator />}
          {group.actions.map((action) => {
            const Icon = action.icon;
            const content = (
              <>
                <Icon className="mr-2 size-4" />
                {action.label}
              </>
            );
            return action.href ? (
              <Item key={action.id} asChild>
                <a href={action.href} target="_blank" rel="noreferrer">
                  {content}
                </a>
              </Item>
            ) : (
              <Item
                key={action.id}
                onClick={action.onSelect}
                className={action.destructive ? 'text-destructive focus:text-destructive' : undefined}
              >
                {content}
              </Item>
            );
          })}
        </Fragment>
      ))}
    </>
  );
}

interface ParticipantActionsMenuProps {
  groups: ParticipantActionGroup[];
  disabled?: boolean;
}

/** The ⋮ button */
export function ParticipantActionsMenu({ groups, disabled }: ParticipantActionsMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" disabled={disabled} aria-label="Acciones">
          <MoreVertical className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52">
        <MenuItems groups={groups} parts={DROPDOWN_PARTS} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface ParticipantRowContextMenuProps {
  /** Called only while the menu is open, so closed rows cost nothing */
  getGroups: () => ParticipantActionGroup[];
  /** The table row to wrap */
  row: ReactElement<{ className?: string; 'data-menu-open'?: boolean }>;
}

/** Right-click menu over a table row (long-press on touch). The row stays highlighted while open. */
export function ParticipantRowContextMenu({ getGroups, row }: ParticipantRowContextMenuProps) {
  const [open, setOpen] = useState(false);
  return (
    <ContextMenu onOpenChange={setOpen}>
      <ContextMenuTrigger asChild>
        {cloneElement(row, { className: cn(row.props.className, open && 'bg-muted/60'), 'data-menu-open': open })}
      </ContextMenuTrigger>
      <ContextMenuContent className="min-w-52">
        <MenuItems groups={getGroups()} parts={CONTEXT_PARTS} />
      </ContextMenuContent>
    </ContextMenu>
  );
}
